import { apiClient, buildIfaceApiUrl, createIfaceHeaders } from "~/utils/apiClient";
import { buildErrorPath, mapErrorCode } from "~/utils/error";
import { CallType, NotificationType, PlanType, ScreenType } from "~/utils/receptionDB.types";

/**
 * APIレスポンスの errorCode を DELIVERY_CONFIRMATION スクリーン用のUIコードに変換する。
 */
export function buildDeliveryReceptionErrorCode(error: unknown, screen: ScreenType): string {
  const rawCode = (error as { response?: { data?: { errorCode?: number | string } } })?.response
    ?.data?.errorCode;
  const numericCode =
    typeof rawCode === "number"
      ? rawCode
      : typeof rawCode === "string"
        ? Number(rawCode) || undefined
        : undefined;
  return mapErrorCode(screen, numericCode);
}

/**
 * callType・extensionNumber に基づき適切なコールバックを実行する。
 * uiCodeFaceTimeConflict・uiCodeNoDestination は呼び出し元のスクリーンプレフィックスを持つ定数を渡す。
 */
export function resolveCallDestinationAction(
  callType: string | null,
  extensionNumber: string,
  isPhoneBookEnabled: boolean,
  uiCodeFaceTimeConflict: string,
  uiCodeNoDestination: string,
  onPhone: () => void,
  onNavigateError: (path: string) => void
): void {
  if (callType === CallType.FaceTime) {
    onNavigateError(
      isPhoneBookEnabled
        ? buildErrorPath(uiCodeFaceTimeConflict)
        : buildErrorPath(uiCodeNoDestination)
    );
    return;
  }
  if (callType === CallType.Phone) {
    if (extensionNumber !== "") {
      onPhone();
    } else {
      onNavigateError(buildErrorPath(uiCodeNoDestination));
    }
    return;
  }
  onNavigateError(buildErrorPath(uiCodeNoDestination));
}

/**
 * plan + notificationType の組み合わせから配達フロー種別を判定する（純粋関数）。
 */
export type DeliveryFlowType = "call" | "chat" | "chatCall" | "unknown";

export function resolveDeliveryFlowType(plan: string, notificationType: string): DeliveryFlowType {
  if (
    plan === PlanType.CALL ||
    (plan === PlanType.CHAT_CALL && notificationType === NotificationType.Call)
  )
    return "call";
  if (
    plan === PlanType.CHAT ||
    (plan === PlanType.CHAT_CALL && notificationType === NotificationType.Chat)
  )
    return "chat";
  if (plan === PlanType.CHAT_CALL && notificationType === NotificationType.ChatAndCall)
    return "chatCall";
  return "unknown";
}

/**
 * CHATフロー: POST APIを呼び出し成功/失敗コールバックを実行する。
 */
export async function executeDeliveryChatPost(
  receptionBody: Record<string, unknown>,
  onSuccess: () => void,
  onError: (error: unknown) => void
): Promise<void> {
  try {
    await apiClient.post(buildIfaceApiUrl("v1/receptions/general"), receptionBody, {
      headers: createIfaceHeaders(true),
    });
    onSuccess();
  } catch (error) {
    onError(error);
  }
}

/**
 * CHAT_CALLプラン時のAPI呼び出し（失敗してもフローを止めない）。
 */
export async function postDeliveryReceptionIgnoreError(
  receptionBody: Record<string, unknown>
): Promise<void> {
  try {
    await apiClient.post(buildIfaceApiUrl("v1/receptions/general"), receptionBody, {
      headers: createIfaceHeaders(true),
    });
  } catch {
    // 失敗しても続行
  }
}
